// src/pages/MembersPage.jsx
import { useState, useEffect, SetStateAction } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea,
  Container, Breadcrumbs, Link, Paper, Avatar, Chip, Button,
  useTheme, alpha, InputAdornment, TextField, MenuItem, FormControl,
  Select, InputLabel, Pagination, Divider, CircularProgress
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  CalendarMonth as CalendarMonthIcon,
  Verified as VerifiedIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

// Import API service
import * as userApi from '../services/user';
import { User } from '../services/user';

const MembersPage = () => {
  const { segment: urlSegment } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // Get segment from query params
  const queryParams = new URLSearchParams(location.search);
  const typeParam = queryParams.get('type');
  const segment = typeParam || urlSegment;

  // States for filtering and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [members, setMembers] = useState<User[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<User[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  
  // Constants
  const itemsPerPage = 12;
  
  // All available filter options - would come from your data source
  const sectors = [
    'Food Processing',
    'Life Sciences & Pharma',
    'Biotechnology',
    'Automotive',
    'Renewables',
    'Machinery & Equipment',
    'Medical Devices & Technology',
    'Minerals & Metals',
    'Chemicals',
    'Building Materials',
    'Aerospace',
    'Maritime',
    'Military',
    'Robotics',
    'Petroleum',
    'Petrochemicals',
    'Mining',
    'Metal Goods',
    'Packaging',
    'Dairy',
    'Dates',
    'Retail & Wholesale',
    'HoReCa',
    'IT, Data & AI',
    'Finance & Banking',
    'Supply Chain & Logistics',
    'Construction & Development',
    'Healthcare Sector',
    'Tourism Sector',
    'Professional Education',
    'Creative Industries',
    'Hospitality Sector',
    'Sport & Lifestyle',
    'Media & Entertainment',
    'R&D & Innovation'
  ];
  
  const segments = [
    'Government', 'Investment Institution', 'Private Sector Company',
    'RDI', 'University', 'Startup', 'Solo-entrepreneur'
  ];
  
  const countries = [
    'Saudi Arabia', 'United Arab Emirates', 'Kuwait', 'Bahrain', 'Qatar', 'Oman',
    'United States', 'United Kingdom', 'Germany', 'France', 'Japan', 'China',
    'India', 'Singapore'
  ];

  // Fetch members from API
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        const data = await userApi.getMembers();
        setMembers(data);
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, []);

  // Filter members based on search term, sector, country, and segment
  useEffect(() => {
    // Filter logic
    let filtered = [...members];

    // Filter by segment if provided
    if (segment && segment !== 'all') {
      filtered = filtered.filter(member =>
        member.segment?.toLowerCase() === segment.toLowerCase()
      );
    }

    // Apply search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(member =>
        member.firstName?.toLowerCase().includes(term) ||
        member.lastName?.toLowerCase().includes(term) ||
        member.title?.toLowerCase().includes(term) ||
        member.organizationName?.toLowerCase().includes(term)
      );
    }

    // Apply sector filter
    if (sectorFilter) {
      filtered = filtered.filter(member =>
        member.selectedSectors?.some(sector =>
          sector.toLowerCase() === sectorFilter.toLowerCase()
        )
      );
    }

    // Apply country filter
    if (countryFilter) {
      filtered = filtered.filter(member =>
        member.country?.toLowerCase() === countryFilter.toLowerCase()
      );
    }

    // Update filtered members
    setFilteredMembers(filtered);

    // Calculate total pages
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));

    // Reset to first page when filters change
    setCurrentPage(1);
  }, [members, searchTerm, sectorFilter, countryFilter, segment]);

  // Handle page change
  const handlePageChange = (event: any, value: SetStateAction<number>) => {
    setCurrentPage(value);
    // Scroll to top when page changes
    window.scrollTo(0, 0);
  };

  // Calculate current page items
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredMembers.slice(startIndex, endIndex);
  };

  // Format date
  const formatDate = (dateString: string | number | Date) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get segment display name
  const getSegmentDisplayName = () => {
    if (!segment) return 'All Members';
    
    // Capitalize first letter of each word
    return segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" color="primary" />}
        aria-label="breadcrumb"
        sx={{
          mb: 4,
          '& .MuiBreadcrumbs-li': {
            display: 'flex',
            alignItems: 'center'
          }
        }}
      >
        <Link
          color="inherit"
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            '&:hover': {
              color: theme.palette.primary.main
            }
          }}
        >
          Home
        </Link>
        <Typography color="text.primary" fontWeight="medium">
          {getSegmentDisplayName()}
        </Typography>
      </Breadcrumbs>

      {/* Hero section */}
      <Paper
        elevation={0}
        sx={{
          mb: 6,
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            opacity: 0.1,
            background: `radial-gradient(circle, ${theme.palette.primary.main} 0%, transparent 70%)`,
            transform: 'translateX(30%)'
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3,
            position: 'relative',
            zIndex: 1
          }}
        >

          <Box>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              {getSegmentDisplayName()}
            </Typography>

            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{
                maxWidth: '800px',
                lineHeight: 1.6,
                mb: 2
              }}
            >
              {segment 
                ? `Connect with professionals from ${getSegmentDisplayName()} organizations. Explore profiles, discover expertise, and build valuable connections in your field.`
                : 'Discover and connect with our diverse network of professionals across all sectors and organization types. Explore profiles, find expertise, and build valuable partnerships.'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filters Section */}
      <Paper
        elevation={2}
        sx={{
          mb: 6,
          p: { xs: 2, md: 3 },
          borderRadius: 3
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name, title, or organization..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="sector-filter-label">Filter by Sector</InputLabel>
              <Select
                labelId="sector-filter-label"
                id="sector-filter"
                value={sectorFilter}
                onChange={(e) => setSectorFilter(e.target.value)}
                label="Filter by Sector"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Sectors</MenuItem>
                {sectors.map((sector) => (
                  <MenuItem key={sector} value={sector}>{sector}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="country-filter-label">Filter by Country</InputLabel>
              <Select
                labelId="country-filter-label"
                id="country-filter"
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                label="Filter by Country"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Countries</MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>{country}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        {/* Filter chips */}
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {searchTerm && (
            <Chip 
              label={`Search: ${searchTerm}`} 
              onDelete={() => setSearchTerm('')}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
          {sectorFilter && (
            <Chip 
              label={`Sector: ${sectorFilter}`} 
              onDelete={() => setSectorFilter('')}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
          {countryFilter && (
            <Chip 
              label={`Country: ${countryFilter}`} 
              onDelete={() => setCountryFilter('')}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
          {segment && (
            <Chip 
              label={`Segment: ${getSegmentDisplayName()}`} 
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
      </Paper>

      {/* Results */}
      <Box sx={{ mb: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: 600, mb: 2 }}
            >
              {filteredMembers.length === 0
                ? 'No members found'
                : `Showing ${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(currentPage * itemsPerPage, filteredMembers.length)} of ${filteredMembers.length} members`}
            </Typography>

            <Grid container spacing={3}>
              {getCurrentPageItems().map((member) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={member.id}>
                  <Card
                    elevation={2}
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 20px rgba(0,0,0,0.12)'
                      },
                      overflow: 'visible'
                    }}
                  >
                    <CardActionArea
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                      onClick={() => navigate(`/member/${member.id}`)}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          pt: 3,
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        <Avatar
                          src={member.photoUrl || `https://ui-avatars.com/api/?name=${member.firstName}+${member.lastName}&background=random&size=150`}
                          alt={`${member.firstName} ${member.lastName}`}
                          sx={{
                            width: 100,
                            height: 100,
                            border: `4px solid ${theme.palette.background.paper}`,
                            boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                          }}
                        />
                      </Box>

                      <CardContent sx={{ pt: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography
                          variant="h6"
                          align="center"
                          gutterBottom
                          sx={{ fontWeight: 600 }}
                        >
                          {`${member.firstName} ${member.lastName}`}
                        </Typography>

                        {member.title && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                            gutterBottom
                            sx={{ mb: 2 }}
                          >
                            {member.title}
                          </Typography>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        {member.organizationName && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <BusinessIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                            <Typography variant="body2" noWrap>
                              {member.organizationName}
                            </Typography>
                          </Box>
                        )}

                        {member.country && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PublicIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                            <Typography variant="body2">
                              {member.country}
                            </Typography>
                          </Box>
                        )}

                        {member.createdAt && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarMonthIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                            <Typography variant="body2">
                              Joined {formatDate(member.createdAt)}
                            </Typography>
                          </Box>
                        )}

                        {member.selectedSectors && member.selectedSectors.length > 0 && (
                          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {member.selectedSectors.slice(0, 2).map((sector, index) => (
                              <Chip
                                key={index}
                                label={sector}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.75rem',
                                  borderRadius: 1
                                }}
                              />
                            ))}
                            {member.selectedSectors.length > 2 && (
                              <Chip
                                label={`+${member.selectedSectors.length - 2}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.75rem',
                                  borderRadius: 1
                                }}
                              />
                            )}
                          </Box>
                        )}

                        <Box sx={{ mt: 'auto', pt: 2, textAlign: 'center' }}>
                          <Typography
                            variant="button"
                            color="primary"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5
                            }}
                          >
                            View Profile
                            <ArrowForwardIcon fontSize="small" />
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* No results message */}
      {filteredMembers.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            border: `1px dashed ${theme.palette.divider}`
          }}
        >
          <Typography variant="h6" gutterBottom color="text.secondary">
            No members found matching your criteria
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or search term to see more results.
          </Typography>
          
          <Button 
            variant="outlined" 
            color="primary" 
            sx={{ mt: 2 }}
            onClick={() => {
              setSearchTerm('');
              setSectorFilter('');
              setCountryFilter('');
            }}
          >
            Clear All Filters
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default MembersPage;